import { HubFeature } from '@ats/api/cm';
import { LocDef, LocDefs, LocMap, LocPortal, MapLocKeys } from '@cm/core/localization';
import { AddressFormLocs } from 'libs/cm/ui/src/lib/components/cm-address-form/address-form.type';

type SpAddressFormLocs =
	| 'SPAddressFormMailingSectionTitle'
	| 'SPAddressFormOtherSectionTitle'
	| 'SPAddressFormLocalizedSectionTitle'
	| 'SPAddressFormEditAddressButton'
	| 'SPAddressFormCancelEditAddressButton'
	| 'SPAddressFormUpdateAddressButton'
	| 'SPAddressFormContentChangesSaved';

type SPSharedAddressFormLocs =
	| 'SPSharedAddressFormLabelCountry'
	| 'SPSharedAddressFormErrorCountryRequired'
	| 'SPSharedAddressFormErrorInvalidCountry'
	| 'SPSharedAddressFormLabelAddress'
	| 'SPSharedAddressFormLabelCity'
	| 'SPSharedAddressFormLabelState'
	| 'SPSharedAddressFormLabelPostal'
	| 'SPSharedAddressFormLabelPhone'
	| 'SPSharedAddressFormLabelFax'
	| 'SPSharedAddressFormErrorPhoneRequired'
	| 'SPSharedAddressFormErrorPhoneVueUsaCanada'
	| 'SPSharedAddressFormErrorPhoneVueNoZeros'
	| 'SPSharedAddressFormErrorPostalRequired'
	| 'SPSharedAddressFormErrorPostalVueUSA'
	| 'SPSharedAddressFormErrorPostalVueCanada'
	| 'SPSharedAddressFormErrorPostalMaxLength'
	| 'SPSharedAddressFormErrorStateRequired'
	| 'SPSharedAddressFormErrorStateRequiredVueHint'
	| 'SPSharedAddressFormErrorStateInvalid'
	| 'SPSharedAddressFormErrorCityRequired'
	| 'SPSharedAddressFormLabelAddressContinued1'
	| 'SPSharedAddressFormLabelAddressContinued2'
	| 'SPSharedAddressFormLabelBusinessAddress'
	| 'SPSharedAddressFormButtonUpdateAddress'
	| 'SPSharedAddressFormButtonCancel'
	| 'SPSharedAddressFormErrorAddressRequired'
	| 'SPSharedAddressFormHintCountry'
	| 'SPSharedAddressFormHintAddress';

const dpRoot = 'Candidates|Summary|Addresses';

const sharedLocDefs: Pick<LocDef, 'htmlAllowed' | 'shared' | 'portal' | 'feature'> = {
	feature: HubFeature.CandidateProfileAddresses,
	htmlAllowed: false,
	shared: false,
	portal: 'SP',
};

export const spSharedAddressFormLocs: LocDefs<SPSharedAddressFormLocs> = {
	SPSharedAddressFormLabelAddress: {
		legacyName: 'CandidateProfile.Addresses/Candidates.Addresses.FieldLabel.Address',
		defaultValue: 'Address',
		description: 'Label for address.',
		displayPath: `${dpRoot}|Address`,
		displayType: 'Label',
		...sharedLocDefs,
	},
	SPSharedAddressFormLabelAddressContinued1: {
		legacyName: 'CandidateProfile.Addresses/Candidates.Addresses.FieldLabel.Address2',
		defaultValue: 'Address (cont.)',
		description: 'Label for address second line.',
		displayPath: `${dpRoot}|Address (line 2)`,
		displayType: 'Label',
		...sharedLocDefs,
	},
	SPSharedAddressFormLabelAddressContinued2: {
		legacyName: 'CandidateProfile.Addresses/Candidates.Addresses.FieldLabel.Address3',
		defaultValue: 'Address (cont.)',
		description: 'Label for address third line.',
		displayPath: `${dpRoot}|Address (line 3)`,
		displayType: 'Label',
		...sharedLocDefs,
	},
	SPSharedAddressFormLabelBusinessAddress: {
		legacyName: 'CandidateProfile.Addresses/Candidates.Addresses.FieldLabel.Business',
		defaultValue: 'This is a Business Address',
		description: 'Label for the business address checkbox.',
		displayPath: `${dpRoot}|Is Business`,
		displayType: 'Label',
		...sharedLocDefs,
	},
	SPSharedAddressFormLabelCity: {
		legacyName: 'CandidateProfile.Addresses/Candidates.Addresses.FieldLabel.City',
		defaultValue: 'City',
		description: 'Error saying the city is required.',
		displayPath: `${dpRoot}|City`,
		displayType: 'Label',
		...sharedLocDefs,
	},
	SPSharedAddressFormLabelCountry: {
		legacyName: 'CandidateProfile.Addresses/Candidates.Addresses.FieldLabel.Country',
		defaultValue: 'Country',
		description: 'Label for country.',
		displayPath: `${dpRoot}|Country`,
		displayType: 'Label',
		...sharedLocDefs,
	},
	SPSharedAddressFormLabelFax: {
		legacyName: 'CandidateProfile.Addresses/Candidates.Addresses.FieldLabel.Fax',
		defaultValue: 'Fax',
		description: 'Label for fax.',
		displayPath: `${dpRoot}|Fax`,
		displayType: 'Label',
		...sharedLocDefs,
	},
	SPSharedAddressFormLabelPhone: {
		legacyName: 'CandidateProfile.Addresses/Candidates.Addresses.FieldLabel.Phone',
		defaultValue: 'Phone',
		description: 'Label for phone.',
		displayPath: `${dpRoot}|Phone`,
		displayType: 'Label',
		...sharedLocDefs,
	},
	SPSharedAddressFormLabelPostal: {
		legacyName: 'CandidateProfile.Addresses/Candidates.Addresses.FieldLabel.PostalCode',
		defaultValue: 'Zip/Postal Code',
		description: 'Label for zip.',
		displayPath: `${dpRoot}|Zip/Postal Code`,
		displayType: 'Label',
		...sharedLocDefs,
	},
	SPSharedAddressFormLabelState: {
		legacyName: 'CandidateProfile.Addresses/Candidates.Addresses.FieldLabel.State',
		defaultValue: 'State/Province',
		description: 'Label for state.',
		displayPath: `${dpRoot}|State`,
		displayType: 'Label',
		...sharedLocDefs,
	},
	SPSharedAddressFormErrorAddressRequired: {
		legacyName: 'CandidateProfile.Addresses/Candidates.Addresses.FieldError.AddressRequired',
		defaultValue: 'Address is required',
		description: 'Error saying the address is required.',
		displayPath: `${dpRoot}|Address Required`,
		displayType: 'Error',
		...sharedLocDefs,
	},
	SPSharedAddressFormErrorCityRequired: {
		legacyName: 'CandidateProfile.Addresses/Candidates.Addresses.FieldError.CityRequired',
		defaultValue: 'City is required',
		description: 'Error saying city is required.',
		displayPath: `${dpRoot}|City Required`,
		displayType: 'Error',
		...sharedLocDefs,
	},
	SPSharedAddressFormErrorCountryRequired: {
		legacyName: 'CandidateProfile.Addresses/Candidates.Addresses.FieldError.CountryRequired',
		defaultValue: 'Country is required',
		description: 'Error saying country is required.',
		displayPath: `${dpRoot}|Country Required`,
		displayType: 'Error',
		...sharedLocDefs,
	},
	SPSharedAddressFormErrorInvalidCountry: {
		legacyName: 'CandidateProfile.Addresses/Candidates.Addresses.FieldError.InvalidCountry',
		defaultValue: 'Invalid country',
		description: 'Error saying the selected country is invalid.',
		displayPath: `${dpRoot}|Invalid Country`,
		displayType: 'Error',
		...sharedLocDefs,
	},
	SPSharedAddressFormErrorStateInvalid: {
		legacyName: 'CandidateProfile.Addresses/Candidates.Addresses.FieldError.StateInvalid',
		defaultValue: 'Invalid state/province',
		description: 'Error saying the selected state is invalid.',
		displayPath: `${dpRoot}|Invalid State`,
		displayType: 'Error',
		...sharedLocDefs,
	},
	SPSharedAddressFormErrorStateRequired: {
		legacyName: 'CandidateProfile.Addresses/Candidates.Addresses.FieldError.StateRequired',
		defaultValue: 'State/Province is required',
		description: 'Error saying state is required.',
		displayPath: `${dpRoot}|State Required`,
		displayType: 'Error',
		...sharedLocDefs,
	},
	SPSharedAddressFormErrorStateRequiredVueHint: {
		defaultValue: 'State is required. If your state is not listed, please type N/A',
		description: 'Error with Vue specific hint for state required.',
		displayPath: `${dpRoot}|State Required Vue Hint`,
		displayType: 'Error',
		htmlAllowed: false,
		shared: false,
		portal: 'SP' as LocPortal,
		feature: HubFeature.CandidateAccessIntegrationEgressPearsonVue,
	},
	SPSharedAddressFormErrorPostalRequired: {
		legacyName:
			'CandidateAccess.Integration.Egress.PearsonVue/CandidateProfile.Addresses.FieldError.PostalRequired',
		defaultValue: 'Zip/Postal Code is required',
		description: 'Error saying the postal code is required.',
		displayPath: `${dpRoot}|Postal Code Required`,
		displayType: 'Error',
		...sharedLocDefs,
		feature: HubFeature.CandidateAccessIntegrationEgressPearsonVue,
	},
	SPSharedAddressFormErrorPostalVueUSA: {
		legacyName:
			'CandidateAccess.Integration.Egress.PearsonVue/CandidateProfile.Addresses.FieldError.PostalUsa',
		defaultValue:
			'Zip/Postal Code must follow one of the two following formats: #####, #####-####',
		description:
			'Error saying the postal code must follow one of the two following formats: #####, #####-####.',
		displayPath: `${dpRoot}|Postal Code USA Format`,
		displayType: 'Error',
		...sharedLocDefs,
		feature: HubFeature.CandidateAccessIntegrationEgressPearsonVue,
	},
	SPSharedAddressFormErrorPostalVueCanada: {
		legacyName:
			'CandidateAccess.Integration.Egress.PearsonVue/CandidateProfile.Addresses.FieldError.PostalCanada',
		defaultValue: 'Zip/Postal Code must follow the following format: A#A#A#',
		description: 'Error saying the postal code must follow the following format: a#a#a#.',
		displayPath: `${dpRoot}|Postal Code Canada Format`,
		displayType: 'Error',
		...sharedLocDefs,
		feature: HubFeature.CandidateAccessIntegrationEgressPearsonVue,
	},
	SPSharedAddressFormErrorPhoneRequired: {
		legacyName:
			'CandidateAccess.Integration.Egress.PearsonVue/CandidateProfile.Addresses.FieldError.PhoneRequired',
		defaultValue: 'Phone number is required',
		description: 'Error saying the phone number is required.',
		displayPath: `${dpRoot}|Phone Number Required`,
		displayType: 'Error',
		...sharedLocDefs,
		feature: HubFeature.CandidateAccessIntegrationEgressPearsonVue,
	},
	SPSharedAddressFormErrorPhoneVueUsaCanada: {
		legacyName:
			'CandidateAccess.Integration.Egress.PearsonVue/CandidateProfile.Addresses.FieldError.PhoneUsa',
		defaultValue: 'Phone number must have 10 numbers',
		description: 'Error saying the phone number must have 10 numbers.',
		displayPath: `${dpRoot}|Phone Number USA Canada Format`,
		displayType: 'Error',
		...sharedLocDefs,
		feature: HubFeature.CandidateAccessIntegrationEgressPearsonVue,
	},
	SPSharedAddressFormErrorPhoneVueNoZeros: {
		legacyName:
			'CandidateAccess.Integration.Egress.PearsonVue/CandidateProfile.Addresses.FieldError.PhoneZeros',
		defaultValue: 'Phone number must not be all zeros',
		description: 'Error saying the phone number must not be all zeros.',
		displayPath: `${dpRoot}|Phone Number No Zeros`,
		displayType: 'Error',
		...sharedLocDefs,
		feature: HubFeature.CandidateAccessIntegrationEgressPearsonVue,
	},
	SPSharedAddressFormErrorPostalMaxLength: {
		defaultValue: 'Maximum number of allowed characters in postal code are: ',
		description: 'Error describing the maximum number of characters in the postal code.',
		displayPath: `${dpRoot}|Postal Code Length`,
		displayType: 'Error',
		...sharedLocDefs,
	},
	SPSharedAddressFormButtonCancel: {
		legacyName: 'CandidateProfile.Addresses/Candidates.Addresses.Button.Cancel',
		defaultValue: 'Cancel',
		description:
			'Button to cancel editing of candidate addresses. This is shared between mailing address, localized address and the other address fields.',
		displayPath: `${dpRoot}|Cancel`,
		displayType: 'Button',
		...sharedLocDefs,
	},
	SPSharedAddressFormButtonUpdateAddress: {
		legacyName: 'CandidateProfile.Addresses/Candidates.Addresses.Button.UpdateAddress',
		defaultValue: 'Update Address',
		description: 'Button to update the address.',
		displayPath: `${dpRoot}|Save`,
		displayType: 'Button',
		...sharedLocDefs,
	},
	SPSharedAddressFormHintCountry: {
		defaultValue: 'To change your country, please contact support.',
		description: 'Hint for country.',
		displayPath: `${dpRoot}|Country Hint`,
		displayType: 'Content',
		htmlAllowed: true,
		shared: false,
		portal: 'SP' as LocPortal,
		feature: HubFeature.CandidateProfileRestrictCandidateCountryUpdates,
	},
	SPSharedAddressFormHintAddress: {
		defaultValue: '',
		description: 'Hint for address.',
		displayPath: `${dpRoot}|Address Hint`,
		displayType: 'Content',
		feature: HubFeature.CandidateProfileAddresses,
		htmlAllowed: false,
		shared: false,
		portal: 'SP' as LocPortal,
	},
};

export const spAddressFormLocs: LocDefs<SpAddressFormLocs> = {
	SPAddressFormMailingSectionTitle: {
		legacyName: 'CandidateProfile.Addresses.Primary/Candidates.Addresses.Title.MailingAddress',
		defaultValue: 'Mailing Address',
		description: 'Title for the mailing address section.',
		displayPath: `${dpRoot}|Mailing Address`,
		displayType: 'Title',
		...sharedLocDefs,
		feature: HubFeature.CandidateProfileAddressesPrimary,
	},
	SPAddressFormOtherSectionTitle: {
		legacyName: 'CandidateProfile.Addresses.Secondary/Candidates.Addresses.Title.OtherAddress',
		defaultValue: 'Other Address',
		description: 'Title for the other address section.',
		displayPath: `${dpRoot}|Other Address`,
		displayType: 'Title',
		...sharedLocDefs,
		feature: HubFeature.CandidateProfileAddressesSecondary,
	},
	SPAddressFormLocalizedSectionTitle: {
		legacyName:
			'CandidateProfile.Addresses.Localized/Candidates.Addresses.Title.LocalizedAddress',
		defaultValue: 'Non-Romanized Address',
		description: 'Title for the non-romanized address section.',
		displayPath: `${dpRoot}|Non-Romanized Address`,
		displayType: 'Title',
		...sharedLocDefs,
		feature: HubFeature.CandidateProfileAddressesLocalized,
	},
	SPAddressFormContentChangesSaved: {
		legacyName: 'CandidateProfile.Addresses/Candidates.Addresses.Notification.ChangesSaved',
		defaultValue: 'Changes saved',
		description: 'Notification shown saying the changes were saved.',
		displayPath: `${dpRoot}|Addresses Changes Saved`,
		displayType: 'Content',
		...sharedLocDefs,
	},

	SPAddressFormEditAddressButton: {
		legacyName: 'CandidateProfile.Addresses/Candidates.Addresses.Button.EditAddress',
		defaultValue: 'Edit',
		description:
			'Button to toggle the edit mode for candidate addresses. This is shared between mailing address, non-Romanized address and the other address fields.',
		displayPath: `${dpRoot}|Edit address`,
		displayType: 'Button',
		...sharedLocDefs,
	},
	SPAddressFormCancelEditAddressButton: {
		legacyName: 'CandidateProfile.Addresses/Candidates.Addresses.Button.Cancel',
		defaultValue: 'Cancel',
		description:
			'Button to cancel editing of candidate addresses. This is shared between mailing address, localized address and the other address fields.',
		displayPath: `${dpRoot}|Cancel`,
		displayType: 'Button',
		...sharedLocDefs,
	},
	SPAddressFormUpdateAddressButton: {
		legacyName: 'CandidateProfile.Addresses/Candidates.Addresses.Button.UpdateAddress',
		defaultValue: 'Update Address',
		description: 'Button to update the address.',
		displayPath: `${dpRoot}|Save`,
		displayType: 'Button',
		...sharedLocDefs,
	},
};

export const ADDRESS_FORM_LOC_KEYS = MapLocKeys<SpAddressFormLocs>(spAddressFormLocs);
export const SHARED_ADDRESS_FORM_LOC_KEYS =
	MapLocKeys<SPSharedAddressFormLocs>(spSharedAddressFormLocs);
export type SpAddressFormLocKeys = LocMap<SpAddressFormLocs>;
export type SpSharedAddressFormLocKeys = LocMap<SPSharedAddressFormLocs>;

export const mappedAddressFormLocs: AddressFormLocs = {
	AddressFormLabelCountry: SHARED_ADDRESS_FORM_LOC_KEYS.SPSharedAddressFormLabelCountry,
	AddressFormLabelFax: SHARED_ADDRESS_FORM_LOC_KEYS.SPSharedAddressFormLabelFax,
	AddressFormErrorCountryRequired:
		SHARED_ADDRESS_FORM_LOC_KEYS.SPSharedAddressFormErrorCountryRequired,
	AddressFormErrorInvalidCountry:
		SHARED_ADDRESS_FORM_LOC_KEYS.SPSharedAddressFormErrorInvalidCountry,
	AddressFormLabelAddress: SHARED_ADDRESS_FORM_LOC_KEYS.SPSharedAddressFormLabelAddress,
	AddressFormLabelCity: SHARED_ADDRESS_FORM_LOC_KEYS.SPSharedAddressFormLabelCity,
	AddressFormLabelState: SHARED_ADDRESS_FORM_LOC_KEYS.SPSharedAddressFormLabelState,
	AddressFormLabelPostal: SHARED_ADDRESS_FORM_LOC_KEYS.SPSharedAddressFormLabelPostal,
	AddressFormLabelPhone: SHARED_ADDRESS_FORM_LOC_KEYS.SPSharedAddressFormLabelPhone,
	AddressFormErrorPhoneRequired:
		SHARED_ADDRESS_FORM_LOC_KEYS.SPSharedAddressFormErrorPhoneRequired,
	AddressFormErrorPhoneVueUsaCanada:
		SHARED_ADDRESS_FORM_LOC_KEYS.SPSharedAddressFormErrorPhoneVueUsaCanada,
	AddressFormErrorPhoneVueNoZeros:
		SHARED_ADDRESS_FORM_LOC_KEYS.SPSharedAddressFormErrorPhoneVueNoZeros,
	AddressFormErrorPostalRequired:
		SHARED_ADDRESS_FORM_LOC_KEYS.SPSharedAddressFormErrorPostalRequired,
	AddressFormErrorPostalVueUSA: SHARED_ADDRESS_FORM_LOC_KEYS.SPSharedAddressFormErrorPostalVueUSA,
	AddressFormErrorPostalVueCanada:
		SHARED_ADDRESS_FORM_LOC_KEYS.SPSharedAddressFormErrorPostalVueCanada,
	AddressFormErrorPostalMaxLength:
		SHARED_ADDRESS_FORM_LOC_KEYS.SPSharedAddressFormErrorPostalMaxLength,
	AddressFormErrorStateRequired:
		SHARED_ADDRESS_FORM_LOC_KEYS.SPSharedAddressFormErrorStateRequired,
	AddressFormErrorStateInvalid: SHARED_ADDRESS_FORM_LOC_KEYS.SPSharedAddressFormErrorStateInvalid,
	AddressFormErrorCityRequired: SHARED_ADDRESS_FORM_LOC_KEYS.SPSharedAddressFormErrorCityRequired,
	AddressFormLabelAddressContinued2:
		SHARED_ADDRESS_FORM_LOC_KEYS.SPSharedAddressFormLabelAddressContinued1,
	AddressFormLabelAddressContinued3:
		SHARED_ADDRESS_FORM_LOC_KEYS.SPSharedAddressFormLabelAddressContinued2,
	AddressFormLabelBusinessAddress:
		SHARED_ADDRESS_FORM_LOC_KEYS.SPSharedAddressFormLabelBusinessAddress,
	AddressFormButtonUpdateAddress:
		SHARED_ADDRESS_FORM_LOC_KEYS.SPSharedAddressFormButtonUpdateAddress,
	AddressFormButtonCancel: SHARED_ADDRESS_FORM_LOC_KEYS.SPSharedAddressFormButtonCancel,
	AddressFormErrorAddressRequired:
		SHARED_ADDRESS_FORM_LOC_KEYS.SPSharedAddressFormErrorAddressRequired,
	AddressFormErrorStateRequiredVueHint:
		SHARED_ADDRESS_FORM_LOC_KEYS.SPSharedAddressFormErrorStateRequiredVueHint,
	AddressFormHintCountry: SHARED_ADDRESS_FORM_LOC_KEYS.SPSharedAddressFormHintCountry,
	AddressFormHintAddress: SHARED_ADDRESS_FORM_LOC_KEYS.SPSharedAddressFormHintAddress,
};
